/** @author PVCOMBANK */
import { Block } from "@block";
import { QueueSpacing, StackSpacing } from "@components/spacing";
import { useDesktopMediaQuery } from "@media-query";
import { spacing } from "@styles/spacing";
import { getLimitRemainSelector } from "app/modules/account-management/reducer/selectors";
import { ArrayCreditShow, getCardValidStatus } from "app/modules/card-manager/card-utils/card-utils";
import { fetchCardDetailRequest, fetchCardListRequest, setCardStore } from "app/modules/card-manager/reducer/actions";
import { getCardListSelector, getCardStoreSelector, getResponseCardDetailSelector } from "app/modules/card-manager/reducer/selectors";
import { amountConfig } from "app/modules/transfer/transfer-utils/transfer.utils";
import { InputAutoComplete, InputSegment, InputText, InputTextCurrency, InputTextMultiline } from "app/shared/components/form/input";
import IconsYellowBanking from "app/shared/components/icons/icon-yellow-banking/IconsYellowBanking";
import { ItemCompleteProps } from "app/shared/components/modal/auto-complete/auto-complete";
import CardSourceModal from "app/shared/components/modal/card-source/card-source.modal";
import { Constant } from "app/shared/constant/constant";
import { ACCOUNT, CARDDETAIL, CARDITEM } from "app/shared/services/services-model.type";
import { nonAccentVietnamese, regexCharacterMarkSpaceDot, replaceEnterKey, replaceSpace } from "app/shared/util/validate";
import React, { useEffect, useMemo, useState } from "react";
import { RegisterOptions, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { StyleSheet } from "react-native";
import { Button, Paragraph, TextInput, useTheme } from "react-native-paper";
import { useDispatch, useSelector } from "react-redux";
import { DataPaymentCardCreate } from "../../reducer/types";

export interface FormPaymentCardProps {
  onSubmit: Function;
  accountDebit: ACCOUNT;
  onCancel: Function;
  requestCreate: Function;
  showModal: Function;
  closeModal: Function;
  cardParam?: CARDITEM;
}

enum FormEnum {
  accountNo,
  cardID,
  cardNum,
  cardName,
  paymentTotal,
  paymentMin,
  paymentForm,
  paymentAmount,
  description,
  paymentType,
}

type ValidDataSchemeFormData = {
  [key in keyof typeof FormEnum]: Omit<RegisterOptions, "valueAsNumber" | "valueAsDate" | "setValueAs">;
};

export interface PaymentCardData {
  accountNo: string;
  cardID: string;
  cardNum: string;
  cardName: string;
  paymentTotal: string;
  paymentMin: string;
  paymentForm: string;
  paymentAmount: string;
  description: string;
  paymentType: string;
}

const FormPaymentCard = (props: FormPaymentCardProps) => {
  const { onSubmit, requestCreate, onCancel, accountDebit, showModal, closeModal, cardParam } = props;
  const { colors } = useTheme();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const isDesktop = useDesktopMediaQuery();

  const cardStore = useSelector(getCardStoreSelector);
  const limitRemainStore = useSelector(getLimitRemainSelector);
  const listCardStore = useSelector(getCardListSelector);
  const cardDetailStore = useSelector(getResponseCardDetailSelector);

  const LIST_CARD_DEBIT_TYPE: ItemCompleteProps[] = [
    { value: Constant.CARD.CARD_DEBIT_TYPE.CARD_TOTAL_DEBIT, label: t("CARD_THANH_TOAN_THE_TIN_DUNG_THANH_TOAN_TOAN_BO_DU_NO") },
    { value: Constant.CARD.CARD_DEBIT_TYPE.CARD_MINIMUM_DEBIT, label: t("CARD_THANH_TOAN_THE_TIN_DUNG_THANH_TOAN_TOI_THIEU") },
    { value: Constant.CARD.CARD_DEBIT_TYPE.CARD_CUSTOM_DEBIT, label: t("CARD_THANH_TOAN_THE_TIN_DUNG_THANH_TOAN_TUY_CHON") },
  ];

  const [validateAmount, setValidateAmount] = useState({ maxAmount: 0, errorMaxDebit: "" });
  const [cardSelected, setCardSelected] = useState<CARDITEM | undefined>();
  const [cardDetail, setCardDetail] = useState<CARDDETAIL | undefined>();
  const [paymentSelectType, setPaymentSelectType] = useState<string>("0");
  const [listCardDebitType, setListCardDebitType] = useState<ItemCompleteProps[]>(LIST_CARD_DEBIT_TYPE);

  // Change name label when select account type;

  // Instance Form Method
  const { control, handleSubmit, setValue, getValues, clearErrors, trigger, watch } = useForm<PaymentCardData>({
    defaultValues: {
      accountNo: "",
      cardNum: "",
      cardName: "",
      paymentTotal: "",
      paymentMin: "",
      paymentForm: Constant.CARD.CARD_DEBIT_TYPE.CARD_CUSTOM_DEBIT,
      paymentAmount: "",
      description: "",
      paymentType: "0",
    },
    mode: "onChange",
  });

  // Tạo validate Schema
  const validateSchemeForm: ValidDataSchemeFormData = {
    accountNo: {
      required: {
        value: true,
        message: t("TRANFER_THONG_BAO_NHAP_THIEU_TAI_KHOAN"),
      },
    },
    paymentType: {
      required: {
        value: false,
        message: "",
      },
    },
    cardID: {
      required: {
        value: false,
        message: "",
      },
    },
    cardNum: {
      required: {
        value: true,
        message: t("CARD_THANH_TOAN_THE_TIN_DUNG_THONG_BAO_VUI_LONG_NHAP_SO_THE"),
      },
      validate: {
        status: (value) => {
          if (value?.length && paymentSelectType === "0") {
            if (cardSelected && !ArrayCreditShow.includes(cardSelected?.hotStatus || "")) {
              return getCardValidStatus(cardSelected?.hotStatus);
            }
          }
          return true;
        },
      },
    },
    cardName: {
      required: {
        value: false,
        message: t("CARD_THANH_TOAN_THE_TIN_DUNG_THONG_BAO_VUI_LONG_NHAP_TEN_CHU_THE"),
      },
    },
    paymentMin: {
      required: {
        value: false,
        message: "",
      },
    },
    paymentTotal: {
      required: {
        value: false,
        message: "",
      },
    },
    paymentForm: {
      required: {
        value: false,
        message: "",
      },
    },
    paymentAmount: {
      required: {
        value: true,
        message: t("CARD_THANH_TOAN_THE_TIN_DUNG_THONG_BAO_VUI_LONG_NHAP_SO_TIEN_THANH_TOAN"),
      },
      validate: {
        amountMin: (value) => {
          const minAmount = cardDetail?.mandatoryAmount && cardDetail?.mandatoryAmount > 0 ? cardDetail?.mandatoryAmount : 1;
          if (Number(value <= 0)) {
            return t("CARD_SO_TIEN_THANH_TOAN_KHONG_HOP_LE").toString();
          } else if (paymentSelectType === "0" && cardDetail && Number(value) < minAmount) {
            return t("CARD_THANH_TOAN_THE_TIN_DUNG_THONG_BAO_LOI_SO_TIEN_THANH_TOAN_TOI_THIEU").toString();
          }
          return true;
        },
        amountMax: (value) => {
          if (validateAmount?.maxAmount && Number(value) > validateAmount?.maxAmount) {
            return validateAmount?.errorMaxDebit;
          }
          return true;
        },
      },
    },
    description: {
      required: {
        value: true,
        message: t("CARD_THANH_TOAN_THE_TIN_DUNG_THONG_BAO_VUI_LONG_NHAP_NOI_DUNG_THANH_TOAN"),
      },
      validate: {
        regexChar: (value) => {
          const regex = regexCharacterMarkSpaceDot();
          if (!regex.test(value)) {
            return t("CARD_THANH_TOAN_THE_TIN_DUNG_THONG_BAO_NOI_DUNG_THANH_TOAN_KHONG_HOP_LE").toString();
          }
          return true;
        },
      },
    },
  };

  const onSelectedDebitType = (item: ItemCompleteProps) => {
    setValue("paymentForm", item.value);
    clearErrors("paymentAmount");
    if (item.value === Constant.CARD.CARD_DEBIT_TYPE.CARD_MINIMUM_DEBIT) {
      setValue("paymentAmount", String(cardDetailStore?.mandatoryAmount || "0"));
    } else if (item.value === Constant.CARD.CARD_DEBIT_TYPE.CARD_TOTAL_DEBIT) {
      setValue("paymentAmount", String(cardDetailStore?.totalAmount || "0"));
    } else {
      setValue("paymentAmount", "");
    }
  };

  const handleRequestCardInfo = React.useCallback(
    (cardID?: string) => {
      if (cardID?.length) {
        dispatch(fetchCardDetailRequest(cardID));
      }
    },
    [dispatch],
  );

  const handleRequestCreate = (value: PaymentCardData): void => {
    const dataUpdate: DataPaymentCardCreate = {
      ACCOUNT_DEBIT: accountDebit,
      CARD:
        value.paymentType === "0"
          ? cardDetailStore
          : {
              cardNum: value.cardNum,
              embossName: value.cardName,
            },
      AMOUNT: value.paymentAmount,
      DESCRIPTION: value.description,
      TYPE: value.paymentType === "0" ? Constant.CARD.CARD_PAYMENT_TYPE.CRD : Constant.CARD.CARD_PAYMENT_TYPE.ACT,
      PAYMENT_TYPE: value.paymentType,
    };
    requestCreate(dataUpdate);
  };

  const handlerSelectCard = React.useCallback(
    (_card: CARDITEM) => {
      closeModal();
      console.log("handlerSelectCard: ", _card);
      setCardSelected(_card);
    },
    [closeModal],
  );

  const onPressCard = () => {
    console.log("onPressCard: ", listCardStore);
    if (!listCardStore?.length && cardStore.statusListCard !== "pending") dispatch(fetchCardListRequest());
    showModal(
      <CardSourceModal
        onPressAcceptCard={handlerSelectCard}
        typeArray={[Constant.CARD.CARD_TYPE.CREDIT]}
        errorMessage={t("CARD_BAN_CHUA_DANG_KI_LOAI_THE_TIN_DUNG_NAY")}
        statusArrayCredit={ArrayCreditShow}
      />,
    );
  };

  const onPressCreate = () => {
    onSubmit();
    handleSubmit(handleRequestCreate).call(() => {});
  };

  // set thông tin chủ thẻ
  const onBlurCardNum = async () => {
    if (getValues("cardNum") !== cardSelected?.cardNum && getValues("paymentType") === "1") {
      setCardSelected({ cardNum: getValues("cardNum") });
    }
  };

  const onChangeTextAmount = (value: string) => {
    const paymentForm = getValues("paymentForm");
    if (value && paymentForm !== Constant.CARD.CARD_DEBIT_TYPE.CARD_CUSTOM_DEBIT) {
      setValue("paymentForm", Constant.CARD.CARD_DEBIT_TYPE.CARD_CUSTOM_DEBIT);
    }
  };

  // fix/replace nội dung chuyển tiền
  const onBlurDescription = () => {
    const value = getValues("description") || "";
    setValue("description", replaceEnterKey(replaceSpace(nonAccentVietnamese(value.trim()))));
    trigger("description");
  };

  // Styles Component
  const styles = useMemo(
    () =>
      StyleSheet.create({
        container: { flex: 1 },
        fieldText: { color: colors.text, fontWeight: "700", fontSize: 16 },
        fieldFromText: {
          color: colors.text,
          width: 200,
          fontWeight: "700",
          fontSize: 16,
        },
        buttonContainer: { width: 128, alignSelf: "center", backgroundColor: colors.notification },
        acceptButtonContainer: { width: 128, alignSelf: "center" },
      }),
    [colors],
  );

  const chooseCard = <TextInput.Icon name={() => <IconsYellowBanking name={"dichvuthe"} size={28} />} size={28} onPress={onPressCard} />;

  const FieldForm = (
    <Block>
      <StackSpacing size="medium" />
      <Paragraph style={styles.fieldFromText}>{t("TRANFER_TAI_KHOAN_NHAN")}</Paragraph>
      <StackSpacing size="medium" />
    </Block>
  );

  const FormAccountType = (
    <InputSegment
      label={t("CARD_THANH_TOAN_THE_TIN_DUNG_KIEU")}
      name="paymentType"
      control={control}
      values={[t("CARD_THANH_TOAN_THE_TIN_DUNG_THANH_TOAN_CA_NHAN"), t("CARD_THANH_TOAN_THE_TIN_DUNG_THANH_TOAN_HO")]}
    />
  );

  const FormsInput = (
    <Block flex={1} bg={colors.backgroundForm} br={12} p={spacing.large}>
      {FormAccountType}
      <StackSpacing size="small" />
      <InputText
        rules={validateSchemeForm.cardNum}
        name="cardNum"
        control={control}
        inputProps={{ right: paymentSelectType === "0" ? chooseCard : null, editable: paymentSelectType === "0" ? false : true }}
        label={t("CARD_THANH_TOAN_THE_TIN_DUNG_SO_THE")}
        onBlur={onBlurCardNum}
      />
      {paymentSelectType === "0" ? (
        <InputText
          rules={validateSchemeForm.cardName}
          name="cardName"
          control={control}
          label={t("CARD_THANH_TOAN_THE_TIN_DUNG_CHU_THE")}
          inputProps={{ editable: false }}
        />
      ) : (
        <></>
      )}
      {paymentSelectType === "0" ? (
        <>
          <Block spaced row={isDesktop ? true : false}>
            <Block flex={1}>
              <InputTextCurrency
                name="paymentTotal"
                keyboardType={"numeric"}
                control={control}
                rules={validateSchemeForm.paymentTotal}
                label={t("CARD_THANH_TOAN_THE_TIN_DUNG_TONG_DU_NO")}
                rightCurrency={t("COMMON_VND")}
                disabledSuggest={true}
                inputProps={{ editable: false }}
                isShowZero={true}
              />
            </Block>
          </Block>
          <QueueSpacing size="huge" />
          <Block flex={1}>
            <InputTextCurrency
              name="paymentMin"
              keyboardType={"numeric"}
              control={control}
              rules={validateSchemeForm.paymentMin}
              label={t("CARD_THANH_TOAN_THE_TIN_DUNG_THANH_TOAN_TOI_THIEU")}
              rightCurrency={t("COMMON_VND")}
              disabledSuggest={true}
              inputProps={{ editable: false }}
              isShowZero={true}
            />
          </Block>

          <InputAutoComplete
            rules={validateSchemeForm.paymentForm}
            name="paymentForm"
            options={listCardDebitType}
            control={control}
            title={t("CARD_THANH_TOAN_THE_TIN_DUNG_HINH_THUC_THANH_TOAN")}
            label={t("CARD_CHON_HINH_THUC_THANH_TOAN")}
            onDismiss={onSelectedDebitType}
          />
        </>
      ) : (
        <></>
      )}
      <InputTextCurrency
        name="paymentAmount"
        keyboardType={"numeric"}
        control={control}
        rules={validateSchemeForm.paymentAmount}
        label={t("CARD_THANH_TOAN_THE_TIN_DUNG_SO_TIEN_THANH_TOAN")}
        rightCurrency={t("COMMON_VND")}
        isShowZero={getValues("paymentForm") === Constant.CARD.CARD_DEBIT_TYPE.CARD_MINIMUM_DEBIT ? true : false}
        onChangedText={onChangeTextAmount}
      />
      <InputTextMultiline
        name="description"
        control={control}
        rules={validateSchemeForm.description}
        label={t("TRANFER_NOI_DUNG_CHUYEN_TIEN")}
        note={t("COMMON_NOTE_DESCRIPTION")}
        onBlur={onBlurDescription}
        inputProps={{ multiline: true, maxLength: 100 }}
        rightAffix={true}
      />
    </Block>
  );

  const ButtonForm = (
    <Block row middle pv={spacing.huge}>
      <Block w={isDesktop ? 200 : 0} />
      <Button onPress={() => onCancel()} mode="contained" style={styles.buttonContainer}>
        {t("COMMON_HUY")}
      </Button>
      <QueueSpacing size="large" />
      <Button mode="contained" style={styles.acceptButtonContainer} onPress={onPressCreate}>
        {t("COMMON_TIEP_TUC")}
      </Button>
    </Block>
  );

  // map nội dung mặc định
  useEffect(() => {
    if (accountDebit.ACCOUNTNAME?.length && !getValues("description").length) {
      const content = accountDebit.ACCOUNTNAME.toUpperCase() + " " + t("CARD_THANH_TOAN_THE_TIN_DUNG").toLowerCase();
      setValue("description", replaceEnterKey(replaceSpace(nonAccentVietnamese(content))));
      clearErrors("description");
    }
  }, [accountDebit, clearErrors, getValues, setValue, t]);

  // card selected
  useEffect(() => {
    setValue("paymentForm", Constant.CARD.CARD_DEBIT_TYPE.CARD_CUSTOM_DEBIT);
    if (getValues("paymentType") === "1") {
      setValue("cardID", "");
      setValue("cardNum", cardSelected?.cardNum || "");
      setValue("cardName", "");
    } else {
      setValue("cardID", cardSelected?.cardId || "");
      setValue("cardNum", cardSelected?.cardNum || "");
      setValue("cardName", cardSelected?.embossName || "");
    }
    clearErrors("paymentForm");
    clearErrors("cardID");
    clearErrors("cardNum");
    clearErrors("cardName");
  }, [cardSelected, setValue, getValues, clearErrors]);

  useEffect(() => {
    if (cardSelected?.cardId?.length) handleRequestCardInfo(cardSelected?.cardId);
  }, [cardSelected, handleRequestCardInfo]);

  // card detail store
  useEffect(() => {
    console.log("cardDetailStore: ", cardDetailStore);
    setCardDetail(cardDetailStore);
    const totalAmount = String(cardDetailStore?.totalAmount || "0");
    const mandatoryAmount = String(cardDetailStore?.mandatoryAmount || "0");
    console.log("totalAmount: ", totalAmount);
    console.log("mandatoryAmount: ", mandatoryAmount);

    setValue("paymentTotal", totalAmount);
    setValue("paymentMin", mandatoryAmount);
    trigger("cardNum");
  }, [cardDetailStore, setValue, trigger]);

  useEffect(() => {
    let list: ItemCompleteProps[] = LIST_CARD_DEBIT_TYPE;
    console.log("cardDetail: ", cardDetail);
    if (cardDetail?.totalAmount === 0) {
      list = [{ value: Constant.CARD.CARD_DEBIT_TYPE.CARD_CUSTOM_DEBIT, label: t("CARD_THANH_TOAN_THE_TIN_DUNG_THANH_TOAN_TUY_CHON") }];
    } else if (cardDetail?.mandatoryAmount === 0) {
      list = [
        { value: Constant.CARD.CARD_DEBIT_TYPE.CARD_TOTAL_DEBIT, label: t("CARD_THANH_TOAN_THE_TIN_DUNG_THANH_TOAN_TOAN_BO_DU_NO") },
        { value: Constant.CARD.CARD_DEBIT_TYPE.CARD_CUSTOM_DEBIT, label: t("CARD_THANH_TOAN_THE_TIN_DUNG_THANH_TOAN_TUY_CHON") },
      ];
    }
    setListCardDebitType(list);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cardDetail, t]);

  useEffect(() => {
    // lấy hạn mức gd ngày/ số dư khả dụng
    const { maxAmount, errorMaxDebit } = amountConfig(accountDebit, limitRemainStore, getValues("accountNo"), true);
    setValidateAmount({ maxAmount, errorMaxDebit });
  }, [limitRemainStore, accountDebit, getValues]);

  useEffect(() => {
    if (validateAmount?.errorMaxDebit?.length && getValues("paymentAmount").length) trigger("paymentAmount");
  }, [getValues, trigger, validateAmount]);

  useEffect(() => {
    if (paymentSelectType === "1") {
      setCardSelected({ cardNum: "" });
    }
    setValue("cardID", "");
    setValue("cardNum", "");
    setValue("cardName", "");
    setValue("paymentTotal", "0");
    setValue("paymentMin", "0");
    setValue("paymentForm", Constant.CARD.CARD_DEBIT_TYPE.CARD_CUSTOM_DEBIT);
    clearErrors("paymentMin");
    clearErrors("paymentTotal");
    clearErrors("paymentForm");
    clearErrors("cardID");
    clearErrors("cardNum");
    clearErrors("cardName");
  }, [clearErrors, dispatch, paymentSelectType, setValue]);

  useEffect(() => {
    if (cardDetailStore && !cardSelected && !cardDetail) {
      dispatch(setCardStore({ ...cardStore, resCardDetail: undefined }));
    }
  }, [dispatch, cardStore, cardDetailStore, cardSelected, cardDetail]);

  useEffect(() => {
    setCardSelected(undefined);
    setCardDetail(undefined);
  }, [paymentSelectType]);

  useEffect(() => {
    if (cardParam?.cardId) {
      console.log("cardParam: ", cardParam);
      setCardSelected(cardParam);
    }
  }, [cardParam]);

  useEffect(() => {
    const subscription = watch(({ paymentType }) => {
      setTimeout(() => {
        setPaymentSelectType(paymentType || "0");
      }, 0);
    });
    return () => subscription.unsubscribe();
  }, [watch]);

  return (
    <>
      <Block row={isDesktop ? true : false} pt={spacing.medium}>
        {FieldForm}
        {FormsInput}
      </Block>
      {ButtonForm}
    </>
  );
};

export default FormPaymentCard;
